/* eslint-disable no-redeclare */
import React, {
  ReactChild,
  useEffect, useRef, useState,
} from 'react';
import {
  ClickAwayListener, Grow, IconButton, MenuItem, MenuList, Paper, Popper, Tooltip,
} from '@mui/material';

export interface MenuItemAction {
  copy: string,
  callBack: () => void,
}
export interface ButtonMenu {
  icon: ReactChild,
  toolTipCopy?: string,
  items: MenuItemAction[],
}

interface Props {
  buttonMenu: ButtonMenu;
  onClose: () => void;
}

const PopperMenuButton: React.FC<Props> = ({ buttonMenu, onClose }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const toolTipCopy = buttonMenu.toolTipCopy ? buttonMenu.toolTipCopy : '';

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current
      && anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpen(false);
    onClose();
  };

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
      onClose();
    } else if (event.key === 'Escape') {
      setOpen(false);
      onClose();
    }
  };

  const prevOpen = React.useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const handleItemOnClick = (e: React.MouseEvent<HTMLLIElement, MouseEvent>, itemCallBack: any) => {
    e.stopPropagation();
    itemCallBack();
    setOpen(false);
  };

  return (
    <div>
      <Tooltip placement="top" disableHoverListener={!buttonMenu.toolTipCopy} title={toolTipCopy} arrow>
        <IconButton
          ref={anchorRef}
          id="composition-button"
          aria-controls={open ? 'composition-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          {buttonMenu.icon}
        </IconButton>
      </Tooltip>
      <Popper
        sx={{ zIndex: 100 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-end"
        transition
        disablePortal
      >
        {({ TransitionProps }) => (
          <Grow
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...TransitionProps}
            style={{
              transformOrigin: 'right top',
            }}
          >
            <Paper data-testid="test-popper-paper" sx={{ marginTop: '4px!important' }} elevation={4}>
              <ClickAwayListener mouseEvent="onMouseDown" touchEvent="onTouchStart" onClickAway={handleClose}>
                <div>
                  <MenuList
                    data-testid="menu-list"
                    sx={{ padding: 0 }}
                    autoFocusItem={open}
                    id="composition-menu"
                    aria-labelledby="composition-button"
                    onKeyDown={handleListKeyDown}
                  >
                    {buttonMenu.items.map((item: MenuItemAction) => (
                      <MenuItem
                        key={item.copy}
                        sx={{ padding: '8px 38px 8px 16px' }}
                        onClick={
                          (
                            e: React.MouseEvent<HTMLLIElement, MouseEvent>,
                          ) => handleItemOnClick(e, item.callBack)
                        }
                      >
                        {item.copy}
                      </MenuItem>
                    ))}
                  </MenuList>
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

export default PopperMenuButton;
